// Phase 9B — BoardDashboardOverview.
//
// Multi-market dashboard shell. Top-level view that shows KPIs
// across all 6 boards, recent activity, and pending approvals
// in a single glanceable surface. This is the "operating system
// home screen" before drilling into individual boards.

import { useMemo } from "react";
import type { CSSProperties } from "react";
import type { BoardType, BoardItem } from "../../boards/useBoardItems.ts";
import {
  useBoardItems,
  useActivityLog,
  usePendingApprovals,
} from "../../boards/useBoardItems.ts";
import { cardLow, cardMedium } from "../../styles/elevation.ts";
import { transitionStandard } from "../../styles/motion.ts";
import * as s from "./board.styles.ts";

// ─── Board summary card ────────────────────────────────────────

interface BoardSummary {
  boardType: BoardType;
  label: string;
  icon: string;
  items: readonly BoardItem[];
}

const BOARD_DEFS: { type: BoardType; label: string; icon: string }[] = [
  { type: "opportunities", label: "Opportunities", icon: "◆" },
  { type: "chains", label: "Chains", icon: "⟐" },
  { type: "risk", label: "Risk", icon: "▲" },
  { type: "results", label: "Results", icon: "◈" },
  { type: "goals", label: "Goals", icon: "◉" },
  { type: "admin", label: "Admin", icon: "⚙" },
];

// ─── Styles ────────────────────────────────────────────────────

const overviewRoot: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-4)",
};

const heroRow: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: "var(--sz-space-3)",
};

const heroCard: CSSProperties = {
  ...cardMedium,
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
  padding: "var(--sz-space-4)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
  borderTop: "3px solid var(--sz-amber)",
};

const heroValue: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(2rem, 4vw, 3rem)",
  letterSpacing: "1px",
  color: "var(--sz-text-primary)",
  lineHeight: 1,
};

const heroLabel: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

const boardSummaryCard: CSSProperties = {
  ...cardLow,
  ...transitionStandard,
  display: "flex",
  alignItems: "center",
  gap: "var(--sz-space-3)",
  padding: "var(--sz-space-3)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
  cursor: "pointer",
};

const boardIcon: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-md)",
  width: "32px",
  height: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--sz-radius-sm)",
  background: "var(--sz-surface-recessed)",
  color: "var(--sz-text-secondary)",
  flexShrink: 0,
};

const boardName: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-md)",
  fontWeight: 600,
  color: "var(--sz-text-primary)",
  flex: 1,
};

const boardStat: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: "1px",
};

const boardStatNum: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  fontWeight: 600,
  color: "var(--sz-text-primary)",
};

const boardStatLabel: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

const sectionHeader: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
};

const sectionTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "1.2rem",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
};

const boardsGrid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: "var(--sz-space-2)",
};

const recentRow: CSSProperties = {
  display: "flex",
  gap: "var(--sz-space-2)",
  alignItems: "baseline",
  padding: "var(--sz-space-1) 0",
  borderBottom: "1px solid var(--sz-border-subtle)",
};

const recentTime: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "10px",
  color: "var(--sz-text-muted)",
  minWidth: "60px",
  flexShrink: 0,
};

const recentAction: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: "var(--sz-amber)",
  minWidth: "70px",
  flexShrink: 0,
};

const recentSummary: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-primary)",
  flex: 1,
};

const twoCol: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "var(--sz-space-4)",
  alignItems: "start",
};

// ─── Helpers ───────────────────────────────────────────────────

function relTime(raw: string): string {
  const diff = Date.now() - new Date(raw).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${String(mins)}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${String(hrs)}h`;
  return `${String(Math.floor(hrs / 24))}d`;
}

// ─── Board summary row ─────────────────────────────────────────

function BoardSummaryRow({
  summary,
  onClick,
}: {
  summary: BoardSummary;
  onClick: (bt: BoardType) => void;
}): JSX.Element {
  const active = summary.items.filter(
    (i) => i.status === "active" || i.status === "executing" || i.status === "live" || i.status === "monitoring",
  ).length;
  const pending = summary.items.filter(
    (i) => i.status === "in_review" || i.status === "queued" || i.status === "qualifying" || i.status === "ready",
  ).length;

  return (
    <div
      style={boardSummaryCard}
      onClick={() => onClick(summary.boardType)}
      role="button"
      tabIndex={0}
    >
      <span style={boardIcon}>{summary.icon}</span>
      <span style={boardName}>{summary.label}</span>
      <div style={boardStat}>
        <span style={boardStatNum}>{String(summary.items.length)}</span>
        <span style={boardStatLabel}>Total</span>
      </div>
      {active > 0 ? (
        <div style={boardStat}>
          <span style={{ ...boardStatNum, color: "var(--sz-success-text)" }}>
            {String(active)}
          </span>
          <span style={boardStatLabel}>Active</span>
        </div>
      ) : null}
      {pending > 0 ? (
        <div style={boardStat}>
          <span style={{ ...boardStatNum, color: "var(--sz-amber)" }}>
            {String(pending)}
          </span>
          <span style={boardStatLabel}>Pending</span>
        </div>
      ) : null}
    </div>
  );
}

// ─── Component ─────────────────────────────────────────────────

export interface BoardDashboardOverviewProps {
  onNavigateBoard: (boardType: BoardType) => void;
}

export function BoardDashboardOverview({
  onNavigateBoard,
}: BoardDashboardOverviewProps): JSX.Element {
  // Fetch all 6 boards
  const opps = useBoardItems("opportunities");
  const chains = useBoardItems("chains");
  const risk = useBoardItems("risk");
  const results = useBoardItems("results");
  const goals = useBoardItems("goals");
  const admin = useBoardItems("admin");
  const activity = useActivityLog(undefined, 10);
  const approvals = usePendingApprovals();

  const allItems = useMemo(
    () => [
      ...opps.items,
      ...chains.items,
      ...risk.items,
      ...results.items,
      ...goals.items,
      ...admin.items,
    ],
    [opps.items, chains.items, risk.items, results.items, goals.items, admin.items],
  );

  const summaries: BoardSummary[] = useMemo(
    () =>
      BOARD_DEFS.map((d) => ({
        boardType: d.type,
        label: d.label,
        icon: d.icon,
        items:
          d.type === "opportunities" ? opps.items
          : d.type === "chains" ? chains.items
          : d.type === "risk" ? risk.items
          : d.type === "results" ? results.items
          : d.type === "goals" ? goals.items
          : admin.items,
      })),
    [opps.items, chains.items, risk.items, results.items, goals.items, admin.items],
  );

  const totalActive = allItems.filter(
    (i) => i.status === "active" || i.status === "executing" || i.status === "live" || i.status === "monitoring",
  ).length;

  const totalPending = allItems.filter(
    (i) => i.status === "in_review" || i.status === "queued" || i.status === "qualifying" || i.status === "ready",
  ).length;

  const resolved = allItems.filter((i) =>
    i.status === "resolved_win" || i.status === "resolved_loss" || i.status === "resolved_void",
  );
  const wins = resolved.filter((i) => i.status === "resolved_win").length;
  const winRate = resolved.length > 0
    ? `${((wins / resolved.length) * 100).toFixed(0)}%`
    : "—";

  return (
    <div style={overviewRoot}>
      {/* Hero KPIs */}
      <div style={heroRow}>
        <div style={heroCard}>
          <span style={heroValue}>{String(allItems.length)}</span>
          <span style={heroLabel}>Total Items</span>
        </div>
        <div style={heroCard}>
          <span style={{ ...heroValue, color: "var(--sz-success-text)" }}>
            {String(totalActive)}
          </span>
          <span style={heroLabel}>Active / Live</span>
        </div>
        <div style={heroCard}>
          <span style={{ ...heroValue, color: "var(--sz-amber)" }}>
            {String(totalPending)}
          </span>
          <span style={heroLabel}>Pending Review</span>
        </div>
        <div style={heroCard}>
          <span style={heroValue}>{winRate}</span>
          <span style={heroLabel}>Win Rate</span>
        </div>
      </div>

      {/* Boards grid */}
      <div>
        <div style={sectionHeader}>
          <span style={sectionTitle}>Boards</span>
          <span style={s.boardCount}>{String(summaries.length)} active</span>
        </div>
        <div style={{ ...boardsGrid, marginTop: "var(--sz-space-2)" }}>
          {summaries.map((sum) => (
            <BoardSummaryRow
              key={sum.boardType}
              summary={sum}
              onClick={onNavigateBoard}
            />
          ))}
        </div>
      </div>

      {/* Two-column: Recent Activity + Pending Approvals */}
      <div style={twoCol}>
        {/* Recent activity */}
        <div>
          <div style={sectionHeader}>
            <span style={sectionTitle}>Recent Activity</span>
            <span style={s.boardCount}>{String(activity.entries.length)}</span>
          </div>
          {activity.entries.length === 0 ? (
            <div style={{ ...s.emptyState, marginTop: "var(--sz-space-2)" }}>
              <span style={s.emptyNote}>No activity recorded yet.</span>
            </div>
          ) : (
            <div style={{ marginTop: "var(--sz-space-2)" }}>
              {activity.entries.slice(0, 8).map((e) => (
                <div key={e.id} style={recentRow}>
                  <span style={recentTime}>{relTime(e.created_at)}</span>
                  <span style={recentAction}>{e.action}</span>
                  <span style={recentSummary}>{e.summary ?? "—"}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pending approvals */}
        <div>
          <div style={sectionHeader}>
            <span style={sectionTitle}>Approvals</span>
            <span style={s.boardCount}>
              {String(approvals.approvals.length)} pending
            </span>
          </div>
          {approvals.approvals.length === 0 ? (
            <div style={{ ...s.emptyState, marginTop: "var(--sz-space-2)" }}>
              <span style={s.emptyNote}>Queue clear. No items awaiting review.</span>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--sz-space-1)", marginTop: "var(--sz-space-2)" }}>
              {approvals.approvals.map((aw) => (
                <div key={aw.id} style={s.approvalBanner}>
                  <span style={s.approvalText}>
                    {aw.requested_action} · {relTime(aw.requested_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <span style={s.phantomBadge}>Phantom · simulated</span>
    </div>
  );
}
